import { type FC, useCallback, useEffect, useState } from "react";

import type {
  DashboardFlowStageJournalEntry,
  DashboardFlowStageJournalListItem,
  DashboardMcpToolRef,
} from "../../dashboard-client/types.gen";

import {
  getFlowStageJournalEntryAsUser,
  listFlowStageJournalAsUser,
} from "../../dashboard-client";
import { formatRelativeTime } from "../../utils/time";
import { HudActionButton } from "../common/HudActionButton";
import { HudPanel } from "../common/HudPanel";
import { HudStatusPill } from "../common/HudStatusPill";
import { LoadingState } from "../common/LoadingState";

type JournalFlowType = "plan_revision" | "execution" | "compile_recording";

interface StageJournalPanelProps {
  flowId: string;
  flowType: JournalFlowType;
}

const statusTone = {
  completed: "success",
  failed: "danger",
  pending: "neutral",
  running: "warning",
} as const;

const statusDot = {
  completed: "bg-emerald-500",
  failed: "bg-rose-500",
  pending: "bg-slate-400",
  running: "bg-amber-500",
} as const;

export const StageJournalPanel: FC<StageJournalPanelProps> = ({
  flowId,
  flowType,
}) => {
  const [entries, setEntries] = useState<DashboardFlowStageJournalListItem[]>(
    [],
  );
  const [detail, setDetail] = useState<DashboardFlowStageJournalEntry | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadEntries = useCallback(async () => {
    const { data } = await listFlowStageJournalAsUser({
      path: { flowId },
      query: { flow_type: flowType, limit: 50 },
    });
    setEntries(data.entries);
  }, [flowId, flowType]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadEntries()
      .catch((loadError: unknown) => {
        if (!cancelled) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : "Failed to load stage journal.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [loadEntries]);

  const loadDetail = useCallback(
    async (entryId: string) => {
      const { data } = await getFlowStageJournalEntryAsUser({
        path: { entryId, flowId },
      });
      setDetail((current) => (current?.id === entryId ? null : data.entry));
    },
    [flowId],
  );

  return (
    <HudPanel
      title="Stage journal"
      actions={
        <HudStatusPill label={String(entries.length)} tone="neutral" hideDot />
      }
      bodyClassName=""
    >
      {loading ? <LoadingState message="Loading stage journal..." /> : null}
      {error ? (
        <div className="border border-rose-500/40 bg-rose-500/5 p-3">
          <HudStatusPill label="Journal Load Failed" tone="danger" />
          <p className="mt-2 font-mono text-[11px] text-rose-700 dark:text-rose-300">
            {error}
          </p>
        </div>
      ) : null}
      {!loading && !error && entries.length === 0 ? (
        <p className="py-6 text-center font-mono text-[11px] uppercase tracking-widest text-slate-400 dark:text-slate-500">
          No journal rows for this flow.
        </p>
      ) : null}
      {!loading && !error && entries.length > 0 ? (
        <div className="divide-y divide-slate-100 dark:divide-white/5">
          {entries
            .toSorted(
              (left, right) => left.stage_sequence - right.stage_sequence,
            )
            .map((entry) => (
              <StageJournalRow
                key={entry.id}
                detail={detail?.id === entry.id ? detail : null}
                entry={entry}
                onToggleDetail={() => void loadDetail(entry.id)}
              />
            ))}
        </div>
      ) : null}
    </HudPanel>
  );
};

const StageJournalRow: FC<{
  detail: DashboardFlowStageJournalEntry | null;
  entry: DashboardFlowStageJournalListItem;
  onToggleDetail: () => void;
}> = ({ detail, entry, onToggleDetail }) => (
  <div className="px-4 py-3">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span
            aria-hidden="true"
            className={`h-2 w-2 flex-shrink-0 rounded-none ${statusDot[entry.stage_status]}`}
          />
          <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-800 dark:text-slate-100">
            {entry.stage_name}
          </span>
          <HudStatusPill
            label={entry.stage_status}
            tone={statusTone[entry.stage_status]}
            hideDot
          />
          {entry.attempt_count > 1 ? (
            <HudStatusPill
              label={`${entry.attempt_count} attempts`}
              tone="warning"
              hideDot
            />
          ) : null}
        </div>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 font-mono text-[10px] uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
          <span>{formatJournalTimeRange(entry)}</span>
          <span>{formatRelativeTime(entry.updated_at)}</span>
        </div>
      </div>
      <HudActionButton
        label={detail ? "Hide Detail" : "Drill"}
        tone="neutral"
        onClick={onToggleDetail}
      />
    </div>
    {entry.anomaly_flags.length > 0 ? (
      <div className="mt-2 flex flex-wrap gap-1.5">
        {entry.anomaly_flags.map((flag) => (
          <HudStatusPill key={flag} label={flag} tone="warning" hideDot />
        ))}
      </div>
    ) : null}
    {entry.stage_status === "failed" && entry.error_summary ? (
      <p className="mt-2 border-l border-rose-500/50 pl-3 font-mono text-[11px] text-rose-700 dark:text-rose-300">
        {entry.error_summary}
      </p>
    ) : null}
    <JournalDrillRefs entry={entry} onToggleDetail={onToggleDetail} />
    {detail ? <StageJournalDetail detail={detail} /> : null}
  </div>
);

const JournalDrillRefs: FC<{
  entry: DashboardFlowStageJournalListItem;
  onToggleDetail: () => void;
}> = ({ entry, onToggleDetail }) => (
  <div className="mt-2 flex flex-wrap gap-1.5">
    {entry.drill_refs.map((ref) => (
      <JournalDrillRef
        key={`${ref.tool}:${ref.id}`}
        entry={entry}
        refItem={ref}
        onToggleDetail={onToggleDetail}
      />
    ))}
  </div>
);

const JournalDrillRef: FC<{
  entry: DashboardFlowStageJournalListItem;
  refItem: DashboardMcpToolRef;
  onToggleDetail: () => void;
}> = ({ entry, refItem, onToggleDetail }) => {
  if (refItem.tool === "get_flow_stage_journal_entry") {
    return (
      <button
        className="border border-slate-200 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500 hover:border-blue-400 hover:text-blue-600 dark:border-slate-800 dark:text-slate-400 dark:hover:border-blue-500 dark:hover:text-blue-300"
        onClick={onToggleDetail}
        type="button"
      >
        {refItem.tool}
      </button>
    );
  }
  return (
    <a
      className="border border-slate-200 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500 hover:border-blue-400 hover:text-blue-600 dark:border-slate-800 dark:text-slate-400 dark:hover:border-blue-500 dark:hover:text-blue-300"
      href={buildDrillHref(refItem, entry)}
    >
      {refItem.tool}
    </a>
  );
};

const StageJournalDetail: FC<{ detail: DashboardFlowStageJournalEntry }> = ({
  detail,
}) => (
  <div className="mt-3 grid gap-2 border border-slate-200 bg-slate-50 p-3 font-mono text-[10px] text-slate-600 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300 sm:grid-cols-2">
    <DetailCell label="Input hash" value={detail.input_hash} />
    <DetailCell label="Output hash" value={detail.output_hash ?? "—"} />
    <DetailCell label="Idempotency" value={detail.idempotency_key} />
    <DetailCell label="Lease" value={formatLease(detail)} />
    <DetailCell label="Task" value={detail.task_id ?? "—"} />
    <DetailCell label="Output ref" value={detail.output_ref ?? "—"} />
  </div>
);

const DetailCell: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="min-w-0">
    <div className="uppercase tracking-[0.16em] text-slate-400">{label}</div>
    <div className="mt-0.5 truncate" title={value}>
      {value}
    </div>
  </div>
);

function formatJournalTimeRange(entry: DashboardFlowStageJournalListItem) {
  const started = formatTimestamp(entry.started_at);
  const completed = formatTimestamp(entry.completed_at);
  return `${started} → ${completed}`;
}

function formatTimestamp(value: number | null) {
  return value ? new Date(value).toLocaleString() : "—";
}

function formatLease(detail: DashboardFlowStageJournalEntry) {
  const owner = detail.lease_owner ?? "none";
  const expires = formatTimestamp(detail.lease_expires_at);
  return `${owner} until ${expires}`;
}

function buildDrillHref(
  refItem: DashboardMcpToolRef,
  entry: DashboardFlowStageJournalListItem,
) {
  const params = new URLSearchParams(window.location.search);
  switch (refItem.tool) {
    case "get_delivery_flow": {
      params.set(
        "overview_flow",
        `${flowPrefix(entry.flow_type)}:${refItem.id}`,
      );
      return `${window.location.pathname}?${params.toString()}`;
    }
    case "get_delivery_task": {
      params.set("taskId", refItem.id);
      return `${window.location.pathname}?${params.toString()}`;
    }
    case "get_delivery_overview": {
      params.set("issueKey", refItem.id);
      return `${window.location.pathname}?${params.toString()}`;
    }
    default: {
      return `${window.location.pathname}?${params.toString()}`;
    }
  }
}

function flowPrefix(flowType: string) {
  if (flowType === "compile_recording") {
    return "cr";
  }
  if (flowType === "plan_revision") {
    return "rev";
  }
  return "exec";
}
